'use client'

import { useCallback } from 'react'
import { QueryKey, UseQueryOptions, useQuery, useQueryClient } from '@tanstack/react-query'

import { useClientApi } from '../useClientApi'

import type { TGetSofInput, TGetSofOutput } from '@/api/v1/resources/sof'

export type TOptionsQuerySof = UseQueryOptions<TGetSofOutput, unknown, TGetSofOutput, QueryKey>

export const getSofKey = (params?: Partial<TGetSofInput>): QueryKey => ['v1', 'sof', params ?? {}]

export const useQuerySof = (input: TGetSofInput | undefined, options?: TOptionsQuerySof) => {
  const { api } = useClientApi()
  const enabled = Boolean(input?.imo || input?.portId)

  return useQuery({
    queryKey: getSofKey(input ?? undefined),
    queryFn: async () => {
      if (!input) throw new Error('Invalid parameters for SOF')
      const usedInput: TGetSofInput = {
        imo: input.imo
      }
      if (input.unlocode) {
        usedInput.unlocode = input.unlocode
      } else {
        usedInput.portId = input.portId
      }
      return api.sof.get(usedInput)
    },
    enabled,
    ...options
  })
}

export default function useSof(input?: TGetSofInput, options?: TOptionsQuerySof) {
  const queryClient = useQueryClient()

  const queryHook = useQuerySof(input, options)

  const invalidate = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: getSofKey(input) })
  }, [queryClient, input])

  return {
    ...queryHook,
    invalidate
  }
}
